<template>
  <div class="spin-wheel-page">
    <div class="page-content">
      <!-- Page Header -->
      <div class="page-header">
        <button class="back-btn" @click="goBack">
          <feather-icon icon="ChevronLeftIcon" size="20" />
        </button>
        <h1 class="page-title">กงล้อมหาโชค</h1>
        <button class="info-btn" @click="showTermsModal = true">
          <feather-icon icon="InfoIcon" size="20" />
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-container">
        <b-spinner variant="warning" />
        <p class="loading-text">กำลังโหลด...</p>
      </div>

      <!-- Main Content -->
      <div v-else class="main-content">
        <!-- Stats Strip (context) -->
        <div class="stats-row">
          <!-- Diamond Balance Card -->
          <div class="stat-card diamond-card">
            <div class="stat-icon">💎</div>
            <div class="stat-value">
              {{ formatNumber(spinStatus.diamonds) }}
            </div>
            <div class="stat-label">เพชรของคุณ</div>
          </div>

          <!-- Spin Counter Card -->
          <div class="stat-card spin-card">
            <div class="stat-icon">🎰</div>
            <div class="stat-value">
              {{ spinStatus.spinsUsed }}/{{ spinStatus.maxSpins }}
            </div>
            <div class="stat-label">หมุนวันนี้</div>
          </div>
        </div>

        <!-- Primary Action Group (spin) -->
        <div class="spin-group">
          <button
            class="spin-btn"
            :class="{ disabled: !canSpin }"
            :disabled="!canSpin"
            @click="openSpinModal"
          >
            <span class="spin-btn-icon">🎡</span>
            <span class="spin-btn-text">{{ spinButtonText }}</span>
          </button>
          <div class="cost-chip">
            <span class="cost-chip-label">ค่าหมุน</span>
            <span class="cost-chip-value">{{ spinStatus.spinCost }} 💎</span>
          </div>
          <p v-if="!canSpin" class="spin-disabled-msg">
            {{ disabledMessage }}
          </p>
        </div>

        <!-- Reward / Coin Wallet Card -->
        <div class="coin-wallet-card">
          <div class="coin-wallet-info">
            <span class="coin-wallet-label">
              <span class="coin-icon">🪙</span> เหรียญของคุณ
            </span>
            <span class="coin-wallet-value">
              {{ formatNumber(spinStatus.coinBalance) }}
              <span class="coin-wallet-unit">เหรียญ</span>
            </span>
          </div>
          <button
            class="exchange-btn"
            :disabled="spinStatus.coinBalance <= 0"
            @click="goToExchange"
          >
            แลกเครดิต
            <feather-icon icon="ChevronRightIcon" size="14" />
          </button>
        </div>

        <!-- History Section -->
        <div class="history-section">
          <div class="section-header">
            <h3 class="section-title">📜 ประวัติการหมุน</h3>
          </div>

          <div class="history-list">
            <div v-if="spinHistory.length === 0" class="empty-state">
              <span class="empty-icon">🎲</span>
              <p class="empty-text">ยังไม่มีประวัติการหมุน</p>
            </div>

            <div v-else class="history-items">
              <div
                v-for="(item, index) in spinHistory"
                :key="item.id"
                class="history-item"
              >
                <div class="history-index">
                  {{ index + 1 }}
                </div>
                <div class="history-info">
                  <div class="history-prize">
                    <span class="prize-icon">🪙</span>
                    <span class="prize-value">{{ item.prize_value }}</span>
                    <span class="prize-name">เหรียญ</span>
                  </div>
                  <div class="history-date">
                    {{ item.created_at }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Spin Wheel Modal -->
      <SpinWheelModal
        v-model="showSpinWheelModal"
        @spin-complete="onSpinComplete"
        @input="onSpinModalClose"
      />

      <!-- Terms & Conditions Modal -->
      <div
        v-if="showTermsModal"
        class="modal-overlay"
        @click.self="showTermsModal = false"
      >
        <div class="terms-modal">
          <div class="modal-header">
            <h3 class="modal-title">📋 เงื่อนไขและรายละเอียด</h3>
            <button class="modal-close" @click="showTermsModal = false">
              ×
            </button>
          </div>

          <div class="modal-body terms-body">
            <div class="terms-content">
              <div class="terms-section">
                <h4 class="terms-heading">🎡 กติกาการหมุนกงล้อ</h4>
                <ul class="terms-list">
                  <li>
                    ผู้เล่นสามารถหมุนกงล้อได้สูงสุด
                    <strong>5 ครั้งต่อวัน</strong>
                  </li>
                  <li>การหมุนแต่ละครั้งใช้เพชร <strong>100 เพชร</strong></li>
                  <li>เพชรที่ใช้หมุนจะถูกหักออกทันทีหลังกดหมุน</li>
                  <li>
                    โควต้าการหมุนจะรีเซ็ตใหม่ทุกวันเวลา
                    <strong>00:00 น.</strong>
                  </li>
                </ul>
              </div>

              <div class="terms-section">
                <h4 class="terms-heading">🪙 รางวัลเหรียญ</h4>
                <ul class="terms-list">
                  <li>
                    รางวัลที่ได้รับจะเป็นเหรียญ ซึ่งสามารถแลกเป็นเครดิตได้
                  </li>
                  <li>อัตราแลกเปลี่ยน: <strong>1 เหรียญ = 1 เครดิต</strong></li>
                  <li>รางวัลที่ได้จะถูกเพิ่มเข้ายอดเหรียญทันที</li>
                </ul>
              </div>

              <div class="terms-section">
                <h4 class="terms-heading">💱 เงื่อนไขการแลกเครดิต</h4>
                <ul class="terms-list">
                  <li>ขั้นต่ำในการแลก: <strong>100 เหรียญ</strong></li>
                  <li>
                    ยอดเครดิตต้องมีน้อยกว่า <strong>10 บาท</strong> ถึงจะแลกได้
                  </li>
                  <li>เครดิตที่แลกจะเข้าบัญชีทันที</li>
                  <li>ไม่สามารถแลกเหรียญกลับเป็นเพชรได้</li>
                  <li>ทำ 3เทิร์น ถอนสูงสุด 6เท่า</li>
                </ul>
              </div>

              <div class="terms-section">
                <h4 class="terms-heading">💎 การได้รับเพชร</h4>
                <ul class="terms-list">
                  <li>เพชรได้รับจากการฝากเงิน และกิจกรรมโปรโมชั่นต่างๆ</li>
                  <li>เพชรไม่สามารถถอนหรือแลกเป็นเงินสดได้โดยตรง</li>
                  <li>เพชรใช้สำหรับหมุนกงล้อเพื่อรับเหรียญเท่านั้น</li>
                </ul>
              </div>

              <div class="terms-section">
                <h4 class="terms-heading">⚠️ ข้อกำหนดทั่วไป</h4>
                <ul class="terms-list">
                  <li>
                    บริษัทขอสงวนสิทธิ์ในการเปลี่ยนแปลงเงื่อนไขโดยไม่ต้องแจ้งให้ทราบล่วงหน้า
                  </li>
                  <li>การตัดสินใจของบริษัทถือเป็นที่สิ้นสุด</li>
                  <li>
                    หากตรวจพบการทุจริต
                    บริษัทขอสงวนสิทธิ์ในการยกเลิกรางวัลทั้งหมด
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="modal-actions">
            <button class="confirm-btn" @click="showTermsModal = false">
              เข้าใจแล้ว
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { BSpinner } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import SpinWheelModal from '../component/SpinWheelModal.vue'

export default {
  name: 'SpinWheelPage',
  components: {
    BSpinner,
    SpinWheelModal
  },
  data() {
    return {
      isLoading: true,
      showSpinWheelModal: false,
      showTermsModal: false,

      // Spin status - initialized empty, loaded from API
      spinStatus: {
        diamonds: 0,
        spinsUsed: 0,
        maxSpins: 5,
        coinBalance: 0,
        spinCost: 100
      },

      // Spin history - loaded from API
      spinHistory: []
    }
  },
  computed: {
    canSpin() {
      return (
        this.spinStatus.diamonds >= this.spinStatus.spinCost &&
        this.spinStatus.spinsUsed < this.spinStatus.maxSpins
      )
    },

    spinButtonText() {
      return 'หมุนกงล้อ'
    },

    disabledMessage() {
      if (this.spinStatus.spinsUsed >= this.spinStatus.maxSpins) {
        return 'หมดโควต้าการหมุนวันนี้แล้ว'
      }
      if (this.spinStatus.diamonds < this.spinStatus.spinCost) {
        return `เพชรไม่เพียงพอ (ต้องการ ${this.spinStatus.spinCost} 💎)`
      }
      return ''
    }
  },
  methods: {
    ...mapActions('member', ['fetchCredit']),

    goBack() {
      this.$router.go(-1)
    },

    formatNumber(num) {
      if (!num && num !== 0) return '0'
      return new Intl.NumberFormat('th-TH').format(num)
    },

    openSpinModal() {
      if (this.canSpin) {
        this.showSpinWheelModal = true
      }
    },

    onSpinModalClose(isOpen) {
      if (!isOpen) {
        // Modal closed, refresh data
        this.fetchSpinStatus()
        this.fetchSpinHistory()
        if (this.$eventBus) {
          this.$eventBus.$emit('refreshBalance')
        }
      }
    },

    onSpinComplete(result) {
      // Update local state immediately when spin completes
      this.spinStatus.diamonds = result.newDiamondBalance
      this.spinStatus.coinBalance = result.newCoinBalance
      this.spinStatus.spinsUsed += 1
    },

    goToExchange() {
      this.$router.push('/coin-exchange').catch(() => {})
    },

    // API functions - integrated with real backend
    async fetchSpinStatus() {
      try {
        const response = await this.$http.get('/member/spin-wheel/info')
        console.log('[SpinWheel] Info API response:', response.data)

        if (response.data && response.data.success && response.data.data) {
          const { data } = response.data
          this.spinStatus = {
            diamonds: data.diamonds?.current_balance || 0,
            spinsUsed: data.diamonds?.daily_spin_count || 0,
            maxSpins: data.diamonds?.max_spins_per_day || 5,
            coinBalance: data.coins?.current_balance || 0,
            spinCost: data.diamonds?.spin_cost || 100
          }
          console.log('[SpinWheel] Parsed spinStatus:', this.spinStatus)
        } else {
          console.warn(
            '[SpinWheel] Unexpected response structure:',
            response.data
          )
        }
      } catch (error) {
        console.error('[SpinWheel] Error fetching spin status:', error)
        this.showErrorToast('ไม่สามารถโหลดข้อมูลได้')
      }
    },

    async fetchSpinHistory() {
      try {
        const response = await this.$http.get(
          '/member/spin-wheel/history?limit=5'
        )
        console.log('[SpinWheel] History API response:', response.data)

        if (response.data && response.data.success && response.data.data) {
          this.spinHistory = response.data.data.history || []
          console.log('[SpinWheel] Parsed history:', this.spinHistory)
        } else {
          console.warn(
            '[SpinWheel] Unexpected history response:',
            response.data
          )
          this.spinHistory = []
        }
      } catch (error) {
        console.error('[SpinWheel] Error fetching spin history:', error)
        this.spinHistory = []
      }
    },

    showErrorToast(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'เกิดข้อผิดพลาด',
          text: message,
          icon: 'AlertCircleIcon',
          variant: 'danger'
        }
      })
    }
  },

  async beforeMount() {
    await Promise.all([
      this.fetchSpinStatus(),
      this.fetchSpinHistory(),
      this.fetchCredit()
    ])
    this.isLoading = false

    if (this.$eventBus) {
      this.$eventBus.$emit('refreshBalance')
    }
  }
}
</script>

<style lang="scss" scoped>
.spin-wheel-page {
  width: 100%;
  min-height: 100vh;
  padding: 8px;
  padding-bottom: 80px;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (min-width: 768px) {
    padding: 12px;
    padding-bottom: 100px;
  }
}

.page-content {
  max-width: 400px;
  margin: 0 auto;
  width: 100%;
}

// Page Header
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 8px 0;
}

.back-btn,
.info-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
  }
}

.page-title {
  font-size: 20px;
  font-weight: bold;
  color: #ffcc00;
  margin: 0;
  text-align: center;
  flex: 1;

  @media (min-width: 768px) {
    font-size: 24px;
  }
}

// Loading State
.loading-container {
  text-align: center;
  padding: 54px 18px;
}

.loading-text {
  color: rgba(255, 255, 255, 0.7);
  margin-top: 14px;
  font-size: 14px;
}

// Main content
.main-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

// Stats Row
.stats-row {
  display: flex;
  gap: 8px;
}

.stat-card {
  flex: 1;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 10px 8px;
  text-align: center;

  &.diamond-card {
    border-color: rgba(0, 191, 255, 0.3);
    background: linear-gradient(
      135deg,
      rgba(0, 191, 255, 0.1),
      rgba(0, 191, 255, 0.05)
    );
  }

  &.spin-card {
    border-color: rgba(255, 204, 0, 0.3);
    background: linear-gradient(
      135deg,
      rgba(255, 204, 0, 0.1),
      rgba(255, 204, 0, 0.05)
    );
  }
}

.stat-icon {
  font-size: 20px;
  margin-bottom: 2px;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 2px;

  .diamond-card & {
    color: #00bfff;
  }

  .spin-card & {
    color: #ffcc00;
  }
}

.stat-label {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
}

// Primary Action Group (spin)
.spin-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px 12px;
  background: linear-gradient(
    135deg,
    rgba(255, 204, 0, 0.08),
    rgba(255, 153, 0, 0.04)
  );
  border: 1px solid rgba(255, 204, 0, 0.2);
  border-radius: 12px;
}

.spin-btn {
  width: 100%;
  max-width: 260px;
  background: linear-gradient(135deg, #ffcc00, #ff9900);
  border: none;
  border-radius: 12px;
  padding: 16px 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(255, 204, 0, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 204, 0, 0.5);
  }

  &:active:not(:disabled) {
    transform: translateY(-1px);
  }

  &.disabled,
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: linear-gradient(135deg, #666, #444);
    box-shadow: none;
  }
}

.spin-btn-icon {
  font-size: 24px;
}

.spin-btn-text {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a2e;
}

.cost-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 204, 0, 0.25);
  border-radius: 999px;
}

.cost-chip-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
}

.cost-chip-value {
  color: #ffcc00;
  font-size: 13px;
  font-weight: 700;
}

.spin-disabled-msg {
  margin: 0;
  color: #ff6b6b;
  font-size: 12px;
  text-align: center;
}

// Reward / Coin Wallet Card
.coin-wallet-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: linear-gradient(
    135deg,
    rgba(40, 167, 69, 0.1),
    rgba(32, 201, 151, 0.05)
  );
  border: 1px solid rgba(40, 167, 69, 0.25);
  border-radius: 12px;
  padding: 14px 14px;
}

.coin-wallet-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.coin-wallet-label {
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
}

.coin-icon {
  font-size: 16px;
}

.coin-wallet-value {
  font-size: 22px;
  font-weight: 800;
  color: #ffd700;
  line-height: 1.1;
}

.coin-wallet-unit {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
}

.exchange-btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: linear-gradient(135deg, #28a745, #20c997);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 10px 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(40, 167, 69, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// History Section
.history-section {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 12px;
}

.section-header {
  margin-bottom: 8px;
}

.section-title {
  color: #ffcc00;
  font-size: 13px;
  font-weight: 500;
  margin: 0;
}

.history-list {
  min-height: 60px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  text-align: center;
}

.empty-icon {
  font-size: 24px;
  margin-bottom: 4px;
  opacity: 0.5;
}

.empty-text {
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  margin: 0;
}

.history-items {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.history-index {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(212, 175, 55, 0.2);
  border-radius: 50%;
  font-size: 10px;
  font-weight: 600;
  color: #d4af37;
  flex-shrink: 0;
}

.history-info {
  flex: 1;
  min-width: 0;
}

.history-prize {
  display: flex;
  align-items: center;
  gap: 4px;

  .prize-icon {
    font-size: 12px;
  }

  .prize-value {
    font-size: 12px;
    font-weight: 700;
    color: #ffd700;
  }

  .prize-name {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.6);
  }
}

.history-date {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
}

// Modal Overlay
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 9999;
  animation: fadeIn 0.15s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-title {
  color: #ffcc00;
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}

.modal-close {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  color: #fff;
  width: 26px;
  height: 26px;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
}

.modal-body {
  padding: 14px 16px;
}

.modal-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 16px 14px 16px;
}

.confirm-btn {
  width: 100%;
  background: linear-gradient(135deg, #28a745, #20c997);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(40, 167, 69, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Terms Modal
.terms-modal {
  background: linear-gradient(
    135deg,
    rgba(16, 16, 30, 0.95),
    rgba(26, 26, 46, 0.95)
  );
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 204, 0, 0.2);
  max-width: 380px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.2s ease-out;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5);
}

.terms-body {
  overflow-y: auto;
  max-height: 60vh;
  padding: 14px 16px !important;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 204, 0, 0.3);
    border-radius: 2px;

    &:hover {
      background: rgba(255, 204, 0, 0.5);
    }
  }
}

.terms-content {
  text-align: left;
}

.terms-section {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.terms-heading {
  color: #ffcc00;
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 8px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 204, 0, 0.2);
}

.terms-list {
  margin: 0;
  padding-left: 18px;
  list-style-type: none;

  li {
    position: relative;
    color: rgba(255, 255, 255, 0.85);
    font-size: 12px;
    line-height: 1.6;
    margin-bottom: 6px;
    padding-left: 4px;

    &::before {
      content: '•';
      position: absolute;
      left: -14px;
      color: #ffcc00;
    }

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      color: #ffcc00;
      font-weight: 600;
    }
  }
}

.terms-text {
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  line-height: 1.6;
  margin: 0;
}
</style>
